import React, { useState, useEffect } from 'react';
import { Image, Folder, ChevronLeft, Search, Loader, Maximize2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ImageSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string, imageHtml?: string) => void;
  mode?: 'featured' | 'content';
}

interface Asset {
  id: string;
  name: string;
  url: string;
  alt_description?: string;
  folder?: string;
}

type ImageSize = 'small' | 'medium' | 'large' | 'full';

const IMAGE_SIZES: Record<ImageSize, { label: string, class: string }> = {
  small: { label: 'Small (25%)', class: 'w-1/4' },
  medium: { label: 'Medium (50%)', class: 'w-1/2' },
  large: { label: 'Large (75%)', class: 'w-3/4' },
  full: { label: 'Full Width', class: 'w-full' }
};

export default function ImageSelectorModal({ isOpen, onClose, onSelect, mode = 'featured' }: ImageSelectorModalProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [folders, setFolders] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedSize, setSelectedSize] = useState<ImageSize>('medium');

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
      fetchFolders();
    } else {
      setSelectedAsset(null);
      setSelectedSize('medium');
      setSearchQuery('');
      setCurrentFolder(null);
    }
  }, [isOpen, currentFolder]);

  const fetchFolders = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('assets')
        .list('', {
          limit: 1000
        });

      if (error) throw error;

      const folderList = (data || [])
        .filter(item => !item.name.includes('.'))
        .map(item => item.name)
        .sort((a, b) => a.localeCompare(b));
      
      setFolders(folderList);
    } catch (err) {
      console.error('Error fetching folders:', err);
      setFolders([]);
    }
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('assets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const filteredAssets = data?.filter(asset => {
        const matchesFolder = currentFolder
          ? asset.folder === currentFolder
          : !asset.folder;

        const matchesSearch = searchQuery
          ? asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (asset.alt_description && asset.alt_description.toLowerCase().includes(searchQuery.toLowerCase()))
          : true;

        return matchesFolder && matchesSearch;
      }) || [];

      setAssets(filteredAssets);
    } catch (err) {
      console.error('Error fetching assets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (e: React.MouseEvent, asset: Asset) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedAsset(asset);
  };

  const handleInsert = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!selectedAsset) return;

    if (mode === 'content') {
      // For content insertion, generate HTML with size class
      const sizeClass = IMAGE_SIZES[selectedSize].class;
      const imageHtml = `<div class="flex justify-center my-4">
  <img 
    src="${selectedAsset.url}" 
    alt="${selectedAsset.alt_description || selectedAsset.name}" 
    class="${sizeClass} rounded-lg shadow-md"
  />
</div>`;
      onSelect(selectedAsset.url, imageHtml);
    } else {
      // For featured image, just pass the URL
      onSelect(selectedAsset.url);
    }
    
    onClose();
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedAsset(null);
    setSelectedSize('medium');
    setSearchQuery('');
    setCurrentFolder(null);
    onClose();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={handleModalClick}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {mode === 'content' ? 'Insert Image' : 'Select Featured Image'}
            </h2>
            {currentFolder && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentFolder(null);
                }}
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="h-5 w-5 mr-1" />
                Back to root
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search images..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
            </div>
          ) : !currentFolder ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {folders.map((folder) => (
                <button
                  key={folder}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentFolder(folder);
                  }}
                  className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <Folder className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-900">{folder}</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {assets.map((asset) => (
              <button
                key={asset.id}
                onClick={(e) => handleSelect(e, asset)}
                className={`group relative aspect-video rounded-lg overflow-hidden border ${
                  selectedAsset?.id === asset.id 
                    ? 'border-[#F98B3D] ring-2 ring-[#F98B3D]' 
                    : 'border-gray-200 hover:border-[#F98B3D]'
                } focus:outline-none transition-all duration-200`}
              >
                <img
                  src={asset.url}
                  alt={asset.alt_description || asset.name}
                  className="w-full h-full object-cover"
                />
                <div className={`absolute inset-0 bg-black transition-opacity duration-200 ${
                  selectedAsset?.id === asset.id ? 'bg-opacity-10' : 'bg-opacity-0 group-hover:bg-opacity-10'
                }`} />
              </button>
            ))}
          </div>

          {assets.length === 0 && (
            <div className="text-center py-12">
              <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No images found</p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            {mode === 'content' && (
              <div className="flex items-center space-x-4">
                <Maximize2 className="h-5 w-5 text-gray-400" />
                <select
                  value={selectedSize}
                  onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedSize(e.target.value as ImageSize);
                  }}
                  className="rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                  disabled={!selectedAsset}
                >
                  {Object.entries(IMAGE_SIZES).map(([value, { label }]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={handleInsert}
              disabled={!selectedAsset}
              className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                selectedAsset
                  ? 'bg-[#F98B3D] text-white hover:bg-[#e07a2c]'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {mode === 'content' ? 'Insert Image' : 'Select Image'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}